// BarChartView — recharts BarChart wrapper with a11y hidden data table.
// Purely presentational: no data fetching, no i18n, no business logic.
// All labels are passed by callers.
import type { CSSProperties } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Label,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { chartPalette } from './chartPalette.js';

export interface BarSeriesItem {
  /** Unique key for each category. */
  key: string;
  /** Category label rendered on the axis. */
  label: string;
  value: number;
}

export interface BarChartViewProps {
  series: BarSeriesItem[];
  /** 'vertical' = columns (bars grow up); 'horizontal' = bars grow right. */
  orientation?: 'vertical' | 'horizontal';
  /** Optional second series; paired to `series` by `key`, not array position. */
  secondSeries?: BarSeriesItem[];
  /** Human-readable label for the primary series (legend + value column). */
  seriesLabel: string;
  /** Label for the second series; the second bar/column renders only when set. */
  secondSeriesLabel?: string;
  /** Category-axis label (SVG label + data table's first column header). */
  xAxisLabel: string;
  /** Numeric-axis label (SVG label only). */
  yAxisLabel?: string;
  /** Accessible label for the chart as a whole. */
  ariaLabel: string;
  height?: number;
}

// WHY: recharts uses layout="horizontal" for vertical bars (columns) and
// layout="vertical" for horizontal bars — the prop name is counter-intuitive.
// Our `orientation` prop uses the visual convention (direction the bars point).
type RechartsLayout = 'horizontal' | 'vertical';

interface BarRow {
  label: string;
  primary: number;
  // null (not 0) marks a missing secondary value so recharts draws a gap.
  secondary?: number | null;
}

// Visually hidden but accessible to AT — the deterministic unit-test hook.
const srOnly: CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  whiteSpace: 'nowrap',
  borderWidth: 0,
};

const axisTickStyle = { fontSize: 11, fill: 'var(--fg-3)' };
const gridStyle = { stroke: 'var(--border-1)', strokeDasharray: '3 3' };
const axisLabelFill = 'var(--fg-3)';

export function BarChartView({
  series,
  orientation = 'vertical',
  secondSeries,
  seriesLabel,
  secondSeriesLabel,
  xAxisLabel,
  yAxisLabel,
  ariaLabel,
  height = 280,
}: BarChartViewProps) {
  const isHorizontal = orientation === 'horizontal';
  const layout: RechartsLayout = isHorizontal ? 'vertical' : 'horizontal';

  // Pair the second series by key so reordered/omitted categories stay aligned.
  const secondByKey = new Map<string, number>();
  if (secondSeries) {
    for (const item of secondSeries) secondByKey.set(item.key, item.value);
  }
  const showSecond = secondSeries !== undefined && secondSeriesLabel !== undefined;

  const data = series.map((item): BarRow => {
    const row: BarRow = { label: item.label, primary: item.value };
    if (showSecond) {
      const sv = secondByKey.get(item.key);
      row.secondary = sv === undefined ? null : sv;
    }
    return row;
  });

  const primary = chartPalette[0] as string;
  const secondary = chartPalette[1] as string;

  return (
    <div>
      {/* Chart SVG is a decorative visual; the data table below is the a11y alternative */}
      <div aria-hidden="true">
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={data} layout={layout}>
            <CartesianGrid {...gridStyle} />
            {isHorizontal ? (
              <>
                <XAxis type="number" tick={axisTickStyle}>
                  {yAxisLabel !== undefined ? (
                    <Label
                      value={yAxisLabel}
                      position="insideBottom"
                      offset={-4}
                      fill={axisLabelFill}
                      fontSize={11}
                    />
                  ) : null}
                </XAxis>
                <YAxis type="category" dataKey="label" tick={axisTickStyle} width={100}>
                  <Label
                    value={xAxisLabel}
                    angle={-90}
                    position="insideLeft"
                    fill={axisLabelFill}
                    fontSize={11}
                  />
                </YAxis>
              </>
            ) : (
              <>
                <XAxis dataKey="label" tick={axisTickStyle}>
                  <Label
                    value={xAxisLabel}
                    position="insideBottom"
                    offset={-4}
                    fill={axisLabelFill}
                    fontSize={11}
                  />
                </XAxis>
                <YAxis tick={axisTickStyle}>
                  {yAxisLabel !== undefined ? (
                    <Label
                      value={yAxisLabel}
                      angle={-90}
                      position="insideLeft"
                      fill={axisLabelFill}
                      fontSize={11}
                    />
                  ) : null}
                </YAxis>
              </>
            )}
            <Tooltip />
            <Legend />
            <Bar dataKey="primary" name={seriesLabel} fill={primary} />
            {secondSeries !== undefined && secondSeriesLabel !== undefined ? (
              <Bar dataKey="secondary" name={secondSeriesLabel} fill={secondary} />
            ) : null}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Visually-hidden data table: a11y text alternative + test hook */}
      <table style={srOnly} aria-label={ariaLabel}>
        <thead>
          <tr>
            <th scope="col">{xAxisLabel}</th>
            <th scope="col" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {seriesLabel}
            </th>
            {showSecond ? (
              <th scope="col" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {secondSeriesLabel}
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {series.map((item) => {
            const sv = secondByKey.get(item.key);
            return (
              <tr key={item.key}>
                <td>{item.label}</td>
                <td style={{ fontVariantNumeric: 'tabular-nums' }}>{item.value}</td>
                {showSecond ? (
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {sv === undefined ? '—' : sv}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
